import { useEffect, useState } from "react";
import useModals from "../../../hooks/useModals";
import Chat from "./Chat";
import { getOneCustomerFile } from "../../../api/customerFile";
import { useSocialStore } from "../../../stores/social";
import { showToastError } from "../../../services/toast";
import { months } from "../../../data/social";
import UploadBulletins from "./UploadBulletins";
import Docs from "./Docs";
import { useUserContext } from "../../../contexts/userContext";
import Validate from "../Validate";
import Refused from "./Refused";
import { parseFrenchDate } from "../../../services/date";
import { useTranslation } from "react-i18next";

const Bulletins = () => {
  const { t } = useTranslation();
  // Context
  const { typeRole } = useUserContext();
  // Store
  const { idEmployee, idCustomer, tabSocial, updateSocial, lastRefusedDate } = useSocialStore();

  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();
  const [customer, setCustomer] = useState<any>(null);
  const [month, setMonth] = useState<string>("");

  // Récupère les informations du client pour afficher le nom de l'entreprise
  useEffect(() => {
    getOneCustomerFile(idCustomer)
      .then((res) => {
        setCustomer(res.data);
        const match = months.find(
          (month: any) => month.value == res?.data?.sc_Month
        );
        setMonth(match?.label || "");
      })
      .catch(() => {
        showToastError(t("Une erreur est survenue"));
      });
  }, [idEmployee, updateSocial, tabSocial]);

  // Fonction helper pour comparer les dates au format français
  // Retourne true si sc_BatchPaySlipDate est plus récente que lastRefusedDate
  const isBatchPaySlipDateOlderThanRefused = (): boolean => {
    if (!lastRefusedDate || !customer?.sc_BatchPaySlipDate) return false;
    
    const refusedDate = parseFrenchDate(lastRefusedDate);
    const batchDate = parseFrenchDate(customer.sc_BatchPaySlipDate);
    
    if (!refusedDate || !batchDate) return false;
    
    // Comparer les timestamps pour être sûr
    const batchTimestamp = batchDate.getTime();
    const refusedTimestamp = refusedDate.getTime();
    
    // Retourner true si batchDate est plus récente que refusedDate
    return batchTimestamp > refusedTimestamp;
  };

  return (
    <div className="w-full h-full flex flex-col bg-linear-to-r from-gray-50 to-gray-100/50">
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null
      )}
      
      {/* En-tête moderne avec effet de carte - Optimisé pour PC portable */}
      <div className="w-full bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-full px-4 py-3">
          <div className="flex justify-between items-center gap-4">
            {/* Section titre avec badge du mois */}
            <div className="md:flex hidden flex-col gap-1.5 min-w-0 shrink">
              <div className="flex items-center gap-2">
                <div className="h-1 w-8 bg-linear-to-r from-blue-500 to-blue-600 rounded-full"></div>
                <h1 className="text-xl font-bold text-gray-800 font-Nunito tracking-tight">
                  {t("Bulletins de paie")}
                </h1>
              </div>
              <div className="flex items-center gap-2 ml-10">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                  {month || t("Mois non défini")}
                </span>
                <span className="text-gray-400 text-sm">•</span>
                <span className="text-gray-600 font-medium text-sm truncate">
                  {customer?.name || t("Chargement...")}
                </span>
              </div>
            </div>

            {/* Boutons d'action modernisés - Compacts pour PC portable */}
            <div className="flex items-center gap-2 shrink-0">
              {typeRole === "Company" && customer?.sc_BatchPaySlipName !== null && (
                <button
                  className="group relative inline-flex items-center gap-1.5 cursor-pointer px-4 py-2 bg-linear-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 ease-out overflow-hidden text-sm"
                  onClick={() =>
                    openModal(
                      "validateBulletins",
                      <Validate
                        closeModal={() => closeModal("validateBulletins")}
                        buttonText="Month"
                      />
                    )
                  }
                >
                  <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-200"></span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t("Valider le mois")}
                </button>
              )}
              
              {customer?.sc_PaySlipsValidated === false && customer?.sc_BatchPaySlipName !== null && typeRole === "Customer" && (!lastRefusedDate || isBatchPaySlipDateOlderThanRefused()) && (
                <>
                  <button
                    className="group relative inline-flex items-center gap-1.5 md:px-4 px-2 py-2 cursor-pointer bg-white text-red-600 font-medium rounded-lg border-2 border-red-200 hover:border-red-300 hover:bg-red-50 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 transition-all duration-200 ease-out md:text-sm text-xs"
                    onClick={() =>
                      openModal(
                        "refusedBulletins",
                        <Refused
                          closeModal={() => closeModal("refusedBulletins")}
                          buttonText="Refused"
                        />
                      )
                    }
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t("Refuser")}
                  </button>
                  
                  <button
                    className="group relative inline-flex items-center gap-1.5 md:px-4 px-2 py-2 bg-linear-to-r cursor-pointer from-green-500 to-green-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 ease-out overflow-hidden md:text-sm text-xs"
                    onClick={() =>
                      openModal(
                        "validateBulletins",
                        <Validate
                          closeModal={() => closeModal("validateBulletins")}
                          buttonText="Accept"
                        />
                      )
                    }
                  >
                    <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-200"></span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t("Valider les bulletins")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal optimisé pour PC portable - espacement réduit */}
      <div className="flex w-full h-full gap-3 p-3 overflow-hidden flex-col md:flex-row">
        <div className="flex-1 h-full min-w-0 transition-all duration-300 ease-in-out hidden md:block">
          <Chat />
        </div>
        <div className="flex-1 h-full min-w-0 transition-all duration-300 ease-in-out">
          <UploadBulletins month={month} customer={customer} />
        </div>
        <div className="flex-1 h-full min-w-0 transition-all duration-300 ease-in-out hidden md:block">
          <Docs />
        </div>
      </div>
    </div>
  );
};

export default Bulletins;
